import { AutoComplete } from 'antd'
import { localityQuery } from 'location/locality'
import { useLocality } from 'location/LocalityProvider'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

import { filtersAPI } from '../../axios/axios'
import {
  MAX_SEARCH_LENGTH,
  normalizeSearchQuery,
  searchObjectPath,
} from './search'

interface Props {
  initialQuery?: string
}

interface Option {
  label: string
  value: string
  disabled?: boolean
}

export const SearchInput = ({ initialQuery = '' }: Props) => {
  const router = useRouter()
  const { locality } = useLocality()
  const [value, setValue] = useState(initialQuery)
  const [options, setOptions] = useState<Option[]>([])
  const [status, setStatus] = useState('')
  const abortRef = useRef<AbortController | null>(null)
  const keyboardSelection = useRef(false)

  useEffect(() => setValue(initialQuery), [initialQuery])

  useEffect(() => {
    const controller = new AbortController()
    abortRef.current = controller
    keyboardSelection.current = false
    setOptions([])
    const phrase = normalizeSearchQuery(value)
    if (!phrase) {
      setStatus('')
      return () => controller.abort()
    }
    if (phrase.length > MAX_SEARCH_LENGTH) {
      setStatus(`Введите не более ${MAX_SEARCH_LENGTH} символов`)
      return () => controller.abort()
    }
    setStatus('Поиск…')
    const timer = setTimeout(async () => {
      try {
        const response = await filtersAPI.mainFilter(phrase, {
          signal: controller.signal,
          params: localityQuery(locality),
          timeout: 15000,
        })
        if (controller.signal.aborted) return
        const items = response.data.result as {
          name: string
          obj_url: string
        }[]
        const nextOptions = items.flatMap((item) => {
          const path = searchObjectPath(item.obj_url)
          return path ? [{ label: item.name, value: path }] : []
        })
        setOptions(nextOptions)
        setStatus(nextOptions.length ? '' : 'Ничего не найдено')
      } catch {
        if (!controller.signal.aborted) {
          setOptions([])
          setStatus('Поиск временно недоступен. Попробуйте ещё раз.')
        }
      }
    }, 250)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [value, locality])

  return (
    <AutoComplete
      aria-label="Поиск программ, мероприятий и организаций"
      showSearch
      allowClear
      value={value}
      onChange={setValue}
      filterOption={false}
      defaultActiveFirstOption={false}
      onDropdownVisibleChange={(open) => {
        if (!open) keyboardSelection.current = false
      }}
      placeholder="Программы, мероприятия или организации"
      style={{ width: '100%' }}
      options={
        status
          ? [{ value: '__search_status__', label: status, disabled: true }]
          : options
      }
      onSelect={(url, option) => {
        const path = searchObjectPath(url)
        if (path) {
          setValue(option.label)
          abortRef.current?.abort()
          router.push(path)
        }
      }}
      onInputKeyDown={(event) => {
        if (
          (event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
          options.length &&
          !status
        )
          keyboardSelection.current = true
        if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
        const input = event.target as HTMLInputElement
        // ArrowDown + Enter передаём AutoComplete для выбора карточки.
        if (
          input.getAttribute('aria-expanded') === 'true' &&
          keyboardSelection.current &&
          !status
        )
          return
        event.preventDefault()
        event.stopPropagation()
        const phrase = normalizeSearchQuery(value)
        if (phrase && phrase.length <= MAX_SEARCH_LENGTH) {
          abortRef.current?.abort()
          input.blur()
          router.push({
            pathname: '/search',
            query: { query: phrase, ...localityQuery(locality) },
          })
        } else if (phrase.length > MAX_SEARCH_LENGTH) {
          setStatus(`Введите не более ${MAX_SEARCH_LENGTH} символов`)
        }
      }}
    />
  )
}
